import { useState, useRef, useEffect, useCallback } from 'react';
import './ImageComparisonSlider.css';

interface ImageComparisonSliderProps {
  beforeImage: string;
  afterImage: string;
  beforeLabel: string;
  afterLabel: string;
}

const ImageComparisonSlider = ({ beforeImage, afterImage, beforeLabel, afterLabel }: ImageComparisonSliderProps) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  const updateSliderPosition = useCallback((clientX: number) => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const position = (x / rect.width) * 100;
    setSliderPosition(Math.min(Math.max(position, 0), 100));
  }, []);

  const handleStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    isDragging.current = true;
    const clientX = e.type === 'touchstart' ? (e as React.TouchEvent).touches[0].clientX : (e as React.MouseEvent).clientX;
    updateSliderPosition(clientX);
  }, [updateSliderPosition]);

  const handleMove = useCallback((e: MouseEvent | TouchEvent) => {
    if (isDragging.current) {
      const clientX = e.type === 'touchmove' ? (e as TouchEvent).touches[0].clientX : (e as MouseEvent).clientX;
      updateSliderPosition(clientX);
    }
  }, [updateSliderPosition]);

  const handleEnd = useCallback(() => {
    isDragging.current = false;
  }, []);

  useEffect(() => {
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleEnd);

    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
    };
  }, [handleMove, handleEnd]);

  return (
    <div
      className="image-comparison-container"
      ref={containerRef}
      onMouseDown={handleStart}
      onTouchStart={handleStart}
    >
      <div className="image-comparison-wrapper">
        <img
          src={beforeImage}
          alt="Before"
          className="image-comparison-before"
        />
        <div
          className="image-comparison-after"
          style={{ clipPath: `inset(0 0 0 ${sliderPosition}%)` }}
        >
          <img
            src={afterImage}
            alt="After"
          />
        </div>
        <div
          className="image-comparison-slider"
          style={{ left: `${sliderPosition}%` }}
        >
          <div className="slider-handle"></div>
        </div>
      </div>
      <div className="image-comparison-labels">
        <span className="before-label">{beforeLabel}</span>
        <span className="after-label">{afterLabel}</span>
      </div>
    </div>
  );
};

export default ImageComparisonSlider; 